// src/lib/pad-visibility.js
//
// Customer pad visibility + ordering (DISPLAY-only). ONE shared rule for every
// customer-facing map + pad list (route pages, destination pages, regions, hotels):
//
//   a pad shows (marker AND list row) only when it is enabled AND served by at
//   least one ACTIVE operator (location.operators[] references an operator with
//   status === "active"), ordered airports first (A–Z) then helipads (A–Z),
//   case-insensitive.
//
// "Active operator" reuses operatorIsActive from aircraft-availability.js — the
// same notion the fleet availability filter uses (paused/inactive don't count).
// Operator/admin views are separate and keep the full pad set + operational notes.

import { operatorIsActive } from './aircraft-availability.js';
import { isOperatorServingPad } from './pricing/eligibility.js';

/** Set of _id for the active operators in a bundle (reuses operatorIsActive). */
export function activeOperatorIdSet(operators) {
  return new Set((operators || []).filter(operatorIsActive).map(o => o._id));
}

/** True if ≥1 of the pad's operators (pad.operatorIds = operators[]->_id) is active. */
export function padHasActiveOperator(pad, operators) {
  if (!pad || !Array.isArray(pad.operatorIds) || !pad.operatorIds.length) return false;
  const ids = activeOperatorIdSet(operators);
  return pad.operatorIds.some(id => ids.has(id));
}

/**
 * Build a per-pad CANONICAL "served by ≥1 active operator" predicate. "Served" is the
 * SHARED isOperatorServingPad union — admin-linked OR self-assigned, minus the
 * operator's unserved opt-out, with adminAssignedOnly respected — i.e. the EXACT
 * predicate the customer Helipad step (opServesPad) and the server money-gate
 * (bookings-create servedBy) use, so customer DISPLAY visibility matches BOOKABILITY.
 *
 * This supersedes padHasActiveOperator for display gating: padHasActiveOperator only
 * checks operatorIds ∩ active and IGNORES the unserved opt-out, so it over-counted —
 * e.g. an operator's parked/unserved base (ZZZZHEL "Athens - Koropi Heliport", which
 * HELISTAR admin-links but has in unservedHelipads) showed publicly yet was never
 * bookable. Returns `(pad) => boolean`; the operator served/unserved Sets are built
 * ONCE here. Pads must carry `_id`, `operatorIds`, `adminAssignedOnly`.
 */
export function buildPadServedPredicate(operators) {
  const active = (operators || []).filter(operatorIsActive);
  const { unservedByOp, servedByOp } = refSets(active);
  return (pad) => !!pad && active.some(o => isOperatorServingPad({
    operatorIds: pad.operatorIds,
    adminAssignedOnly: !!pad.adminAssignedOnly,
    opId: o._id,
    padId: pad._id,
    servedSet: servedByOp[o._id],
    unservedSet: unservedByOp[o._id],
  }));
}

/**
 * Filter + order pads for customer display. Pads must carry `enabled` and
 * `hasServingActiveOperator` (precomputed once on the bundle via
 * buildPadServedPredicate — see LOCATION_DB / the SSR padList). Keeps only enabled +
 * canonically-served pads (an unserved pad is hidden everywhere it would otherwise be
 * named/listed); orders airports first (A–Z) then helipads (A–Z), case-insensitive.
 */
export function customerVisiblePads(pads) {
  return (pads || [])
    .filter(p => p && p.enabled && p.hasServingActiveOperator)
    .slice()
    .sort((a, b) => {
      const typeDiff = (a.type === 'airport' ? 0 : 1) - (b.type === 'airport' ? 0 : 1);
      if (typeDiff !== 0) return typeDiff;
      return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    });
}

// opId -> Set(location._id) maps from operator served/unserved arrays. Mirrors the
// EXACT ref-extraction in App.jsx buildUnservedByOp/buildServedByOp (bundle shape
// `[{ ref }]`; legacy `{ helipadRef }` tolerated) so the SERVING truth is identical
// to the Helipad step + the server money-gate (all via isOperatorServingPad).
function refSets(operators) {
  const unservedByOp = {}, servedByOp = {};
  const refOf = (x) => x && (x.ref || x.helipadRef?._ref || x.helipadRef || (typeof x === 'string' ? x : null));
  for (const o of (operators || [])) {
    unservedByOp[o._id] = new Set((o.unservedHelipads || []).map(refOf).filter(Boolean));
    servedByOp[o._id] = new Set((o.servedHelipads || []).map(refOf).filter(Boolean));
  }
  return { unservedByOp, servedByOp };
}

/**
 * Distinct `location.city` over ENABLED pads that ≥1 ACTIVE operator SERVES, where
 * "serves" is the SHARED isOperatorServingPad union (admin-linked OR self-assigned,
 * minus unserved) — the SAME predicate the customer Helipad step (opServesPad) and
 * the server money-gate (bookings-create servedBy) use. NOT `hasActiveOperator`,
 * which ignores the served/unserved toggle and would over-count.
 *
 * Feeds the charter Departure/Arrival city dropdowns: a city shows iff a serving pad
 * exists there; an unserved pad (e.g. an operator's parked base) never grants city
 * visibility. Pads + operators come from the SAME customer bundle as the predicate,
 * so the list rebuilds correctly whenever an operator toggles serving.
 *
 * @param {Array} locations — LOCATION_DB entries ({ _id, city, enabled, operatorIds, adminAssignedOnly })
 * @param {Array} operators — OPERATOR_FLEET entries ({ _id, status, unservedHelipads, servedHelipads })
 * @returns {Set<string>} set of city names with ≥1 served pad
 */
export function servedCities(locations, operators) {
  const active = (operators || []).filter(operatorIsActive);
  const { unservedByOp, servedByOp } = refSets(active);
  const cities = new Set();
  for (const pad of (locations || [])) {
    if (!pad || pad.enabled === false || !pad.city || cities.has(pad.city)) continue;
    const served = active.some(o => isOperatorServingPad({
      operatorIds: pad.operatorIds, adminAssignedOnly: !!pad.adminAssignedOnly,
      opId: o._id, padId: pad._id,
      servedSet: servedByOp[o._id], unservedSet: unservedByOp[o._id],
    }));
    if (served) cities.add(pad.city);
  }
  return cities;
}

/**
 * Distinct `location.city` over ENABLED pads that ≥1 ACTIVE operator offers
 * SIGHTSEEING from — i.e. the pad is in the operator's `sightseeingHelipads` AND the
 * operator has a sightseeing-enabled aircraft. Mirrors App.jsx `sightseeingServes`.
 * Feeds the sightseeing Departure dropdown (replaces the tour-content-driven source).
 *
 * @param {Array} locations — LOCATION_DB entries ({ _id, city, enabled })
 * @param {Array} operators — OPERATOR_FLEET entries ({ _id, status, sightseeingHelipads, acPricing:[{sightseeingEnabled}] })
 * @returns {Set<string>} set of city names with ≥1 sightseeing-served pad
 */
export function sightseeingServedCities(locations, operators) {
  const active = (operators || []).filter(operatorIsActive);
  const cities = new Set();
  for (const pad of (locations || [])) {
    if (!pad || pad.enabled === false || !pad.city || cities.has(pad.city)) continue;
    const served = active.some(o =>
      (o.sightseeingHelipads || []).some(h => (h?.helipadRef?._ref || h?.helipadRef || h) === pad._id)
      && (o.acPricing || []).some(a => a && a.sightseeingEnabled));
    if (served) cities.add(pad.city);
  }
  return cities;
}
